(() => {
  const DEFAULT_CASE_SIZE = 12;

  const toPositiveInteger = (value, fallback = 1) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  };

  const getProductRoot = (element) => element?.closest?.('product-info') || document;

  const getPackSelector = (element) => {
    const root = getProductRoot(element);
    return root.querySelector('product-pack-selector');
  };

  class ProductPackSelector extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.form = document.getElementById(this.dataset.productFormId);
      this.productRoot = getProductRoot(this);
      this.quantityInput = this.productRoot.querySelector('[data-pack-display-quantity]');
      this.statusElement = this.querySelector('[data-pack-status]');
      this.modeInputs = Array.from(this.querySelectorAll('input[name^="duplin-pack-mode-"]'));
      this.singleInput = this.modeInputs.find((input) => input.value === 'single');
      this.caseInput = this.modeInputs.find((input) => input.value === 'case');
      this.caseSize = toPositiveInteger(this.dataset.caseSize, DEFAULT_CASE_SIZE);
      this.inventoryUnlimited = this.dataset.inventoryUnlimited === 'true';

      this.modeInputs.forEach((input) => input.addEventListener('change', () => this.onModeChange()));
      this.quantityInput?.addEventListener('input', () => this.onQuantityChange());
      this.quantityInput?.addEventListener('change', () => this.onQuantityChange());

      this.sellingPlanChangeHandler = (event) => {
        const sellingPlanId = event.detail?.selling_plan_id ?? event.detail?.sellingPlanId ?? '';
        this.setSubscriptionSelected(Boolean(String(sellingPlanId).trim()));
      };
      this.sellingPlanInputChangeHandler = (event) => {
        if (!event.target?.matches?.('input[name="allure_selling_plan_id"]')) return;
        this.setSubscriptionSelected(Boolean(String(event.target.value || '').trim()));
      };
      document.addEventListener('allure:selling_plan_change', this.sellingPlanChangeHandler);
      document.addEventListener('change', this.sellingPlanInputChangeHandler);
      this.updateSubscriptionStateFromDom();

      const initialInventory = Number.parseInt(this.dataset.inventoryQuantity, 10);
      if (!this.inventoryUnlimited && Number.isFinite(initialInventory)) {
        this.setInventory(initialInventory);
      } else {
        this.syncFormFields();
        this.renderStatus();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('allure:selling_plan_change', this.sellingPlanChangeHandler);
      document.removeEventListener('change', this.sellingPlanInputChangeHandler);
    }

    get mode() {
      return this.modeInputs.find((input) => input.checked)?.value === 'case' ? 'case' : 'single';
    }

    get multiplier() {
      return this.mode === 'case' ? this.caseSize : 1;
    }

    get cartQuantity() {
      return Math.max(0, Number.parseInt(this.quantityInput?.dataset.cartQuantity || '0', 10) || 0);
    }

    get remainingInventory() {
      if (this.inventoryUnlimited || !Number.isFinite(this.inventoryQuantity)) return null;
      return Math.max(0, this.inventoryQuantity - this.cartQuantity);
    }

    get displayMaximum() {
      const remaining = this.remainingInventory;
      if (remaining === null) return null;
      return Math.floor(remaining / this.multiplier);
    }

    get displayQuantity() {
      const fallback = this.displayMaximum === 0 ? 0 : 1;
      const parsed = Number.parseInt(this.quantityInput?.value, 10);
      return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
    }

    onModeChange() {
      if (this.quantityInput) this.quantityInput.value = this.displayMaximum === 0 ? 0 : 1;
      this.applyQuantityLimit();
      this.syncFormFields();
      this.renderStatus();

      document.dispatchEvent(
        new CustomEvent('duplin:pack-mode-changed', {
          detail: {
            formId: this.dataset.productFormId,
            mode: this.mode,
            multiplier: this.multiplier,
          },
        })
      );
    }

    onQuantityChange() {
      this.applyQuantityLimit();
      this.syncFormFields();
    }

    updateSubscriptionStateFromDom() {
      const selectedPlan = this.productRoot.querySelector('input[name="allure_selling_plan_id"]:checked');
      this.subscriptionSelected = Boolean(String(selectedPlan?.value || '').trim());
    }

    setSubscriptionSelected(subscriptionSelected) {
      const changed = this.subscriptionSelected !== subscriptionSelected;
      this.subscriptionSelected = subscriptionSelected;

      if (subscriptionSelected && this.mode === 'case') {
        if (this.singleInput) this.singleInput.checked = true;
        if (this.quantityInput) this.quantityInput.value = '1';
      }

      this.applyQuantityLimit();
      this.syncFormFields();
      this.renderStatus();

      if (changed) {
        document.dispatchEvent(
          new CustomEvent('duplin:pack-mode-changed', {
            detail: {
              formId: this.dataset.productFormId,
              mode: this.mode,
              multiplier: this.multiplier,
            },
          })
        );
      }
    }

    setInventory(maxQuantity) {
      const parsed = Number.parseInt(maxQuantity, 10);
      if (!Number.isFinite(parsed)) return;

      this.inventoryQuantity = Math.max(0, parsed);
      if (this.quantityInput) this.quantityInput.setAttribute('avail-qty', String(this.inventoryQuantity));
      this.applyQuantityLimit();
      this.syncFormFields();
      this.renderStatus();
    }

    applyQuantityLimit() {
      if (!this.quantityInput) return;

      const remaining = this.remainingInventory;
      const availableCases = remaining === null ? null : Math.floor(remaining / this.caseSize);
      if (this.caseInput) {
        const caseUnavailable = this.subscriptionSelected || (availableCases !== null && availableCases < 1);
        this.caseInput.disabled = caseUnavailable;
        this.caseInput.closest('label')?.classList.toggle('is-disabled', caseUnavailable);
        this.caseInput.setAttribute('aria-disabled', caseUnavailable ? 'true' : 'false');
      }

      const maximum = this.displayMaximum;
      if (maximum === null) {
        this.quantityInput.removeAttribute('max');
        this.quantityInput.removeAttribute('data-max');
        this.quantityInput.min = '1';
        this.quantityInput.dataset.min = '1';
        this.updateSubmitState(false);
        return;
      }

      const minimum = maximum === 0 ? 0 : 1;
      this.quantityInput.min = String(minimum);
      this.quantityInput.dataset.min = String(minimum);
      this.quantityInput.max = String(maximum);
      this.quantityInput.dataset.max = String(maximum);

      let current = this.displayQuantity;
      if (current < minimum) current = minimum;
      if (current > maximum) current = maximum;
      this.quantityInput.value = String(current);
      this.updateSubmitState(maximum === 0);
    }

    updateSubmitState(unavailable) {
      const submitButton = this.form?.querySelector('[type="submit"][name="add"]');
      if (!submitButton) return;

      if (unavailable) {
        if (submitButton.dataset.packUnavailable !== 'true') {
          submitButton.dataset.packWasDisabled = submitButton.disabled ? 'true' : 'false';
        }
        submitButton.dataset.packUnavailable = 'true';
        submitButton.disabled = true;
        submitButton.setAttribute('aria-disabled', 'true');
        return;
      }

      if (submitButton.dataset.packUnavailable === 'true') {
        const wasDisabled = submitButton.dataset.packWasDisabled === 'true';
        delete submitButton.dataset.packUnavailable;
        delete submitButton.dataset.packWasDisabled;
        if (!wasDisabled && !this.form?.querySelector('.product-variant-id')?.disabled) {
          submitButton.disabled = false;
          if (submitButton.dataset.pickupUnavailable !== 'true') submitButton.removeAttribute('aria-disabled');
        }
      }
    }

    syncFormFields() {
      if (!this.form || !this.quantityInput) return;

      const displayQuantity = this.displayQuantity;
      const actualQuantity = displayQuantity * this.multiplier;
      const actualQuantityInput = this.form.querySelector('[data-pack-actual-quantity]');
      const modeInput = this.form.querySelector('[data-pack-mode-property]');
      const sizeInput = this.form.querySelector('[data-pack-size-property]');
      const descriptionInput = this.form.querySelector('[data-pack-description-property]');
      const caseCountInput = this.form.querySelector('[data-pack-case-count-property]');

      if (actualQuantityInput) actualQuantityInput.value = String(actualQuantity);
      if (modeInput) modeInput.value = this.mode;
      if (sizeInput) sizeInput.value = String(this.multiplier);
      if (descriptionInput) {
        descriptionInput.value = this.mode === 'case'
          ? `Case — ${this.caseSize} bottles per case`
          : 'Single bottle';
      }
      if (caseCountInput) {
        caseCountInput.value = this.mode === 'case'
          ? `${displayQuantity} ${displayQuantity === 1 ? 'case' : 'cases'} (${actualQuantity} bottles)`
          : '';
      }

      const quantityLabel = this.productRoot.querySelector('[data-pack-quantity-label]');
      if (quantityLabel) quantityLabel.textContent = this.mode === 'case' ? 'Cases' : 'Bottles';
      this.quantityInput.setAttribute(
        'aria-label',
        this.mode === 'case'
          ? `Number of cases. Each case contains ${this.caseSize} bottles.`
          : 'Number of single bottles.'
      );
    }

    renderStatus() {
      if (!this.statusElement) return;

      if (this.subscriptionSelected) {
        this.statusElement.textContent = 'Case purchasing is available for one-time purchases only.';
        return;
      }

      const remaining = this.remainingInventory;
      if (this.mode === 'case') {
        const availableCases = remaining === null ? null : Math.floor(remaining / this.caseSize);
        if (availableCases === 0) {
          this.statusElement.textContent = `Not enough stock for a full case. Choose Single; one case requires ${this.caseSize} bottles.`;
        } else if (availableCases !== null) {
          this.statusElement.textContent = `1 case contains ${this.caseSize} bottles. Maximum ${availableCases} full ${availableCases === 1 ? 'case' : 'cases'} with current stock.`;
        } else {
          this.statusElement.textContent = `1 case contains ${this.caseSize} bottles. Cart and checkout quantities are shown as bottles.`;
        }
      } else if (remaining !== null && remaining < this.caseSize) {
        this.statusElement.textContent = `Only ${remaining} ${remaining === 1 ? 'bottle is' : 'bottles are'} currently available; a full case requires ${this.caseSize}.`;
      } else {
        this.statusElement.textContent = `Choose Case to add ${this.caseSize} bottles at a time.`;
      }
    }
  }

  if (!customElements.get('product-pack-selector')) {
    customElements.define('product-pack-selector', ProductPackSelector);
  }

  window.DuplinPack = {
    caseSize: DEFAULT_CASE_SIZE,

    getSelector: getPackSelector,

    getMultiplier(element) {
      return getPackSelector(element)?.multiplier || 1;
    },

    sync(element) {
      const selector = getPackSelector(element);
      if (!selector) return false;
      selector.syncFormFields();
      return true;
    },

    applyInventoryLimit(element, maxQuantity) {
      const selector = getPackSelector(element);
      if (!selector) return false;
      selector.setInventory(maxQuantity);
      return true;
    },

    recordCartAddition(element, addedQuantity) {
      const selector = getPackSelector(element);
      if (!selector?.quantityInput) return false;
      const added = Math.max(0, Number.parseInt(addedQuantity, 10) || 0);
      selector.quantityInput.dataset.cartQuantity = String(selector.cartQuantity + added);
      if (Number.isFinite(selector.inventoryQuantity)) selector.setInventory(selector.inventoryQuantity);
      return true;
    },

    refreshAfterCartAdd(element) {
      const selector = getPackSelector(element);
      if (!selector?.quantityInput) return false;
      const available = Number.parseInt(selector.quantityInput.getAttribute('avail-qty'), 10);
      if (Number.isFinite(available)) selector.setInventory(available);
      return true;
    },
  };
})();
